import React, { Component } from 'react';
import { Helmet } from 'react-helmet';
import { Grid } from 'react-bootstrap';
import { withApollo, graphql, compose } from 'react-apollo';
import gql from 'graphql-tag';
import Game from '../../components/Game';
import CloseIcon from '../../components/CloseIcon';
import Leaderboard from '../../components/Leaderboard';
import CommunityMessageContainer from '../../containers/CommunityMessageContainer';
import './TournamentPage.css';
import GameChat from '../../components/GameChat/index';
import { getUserData } from '../../utils/graphQLFunctions';
import fragments from '../../utils/graphql/fragments';
import cloneDeep from 'lodash/cloneDeep';
import { GLOBAL_MESSAGES } from 'utils/constants';
import ErrorNotificationPage from 'components/ErrorNotificationPage';
import { SUBSCRIPTION_TOURNAMENT_EVENTS } from 'containers/TournamentList';

const SUBSCRIPTION_UPDATE_DELAY = 4000;

class TournamentPage extends Component {
  constructor(props) {
    super(props);

    this.state = {
      notificationMessage: '',
      tournamentEnded: false
    };

    this.onTournamentEnd = this.onTournamentEnd.bind(this);
    this.onGameStartError = this.onGameStartError.bind(this);
    this.unsubscribeFromTournamentUpdates = () => {};
    this.unsubscribeFromTournamentEvents = () => {};
  }

  componentDidMount() {
    this.unsubscribeFromTournamentUpdates = this.props.subscribeToTournamentUpdates();
    this.unsubscribeFromTournamentEvents = this.props.subscribeToTournamentEvents(
      this.onTournamentEnd
    );
  }

  componentWillUnmount() {
    this.unsubscribeFromTournamentUpdates();
    this.unsubscribeFromTournamentUpdates = () => {};

    this.unsubscribeFromTournamentEvents();
    this.unsubscribeFromTournamentEvents = () => {};
  }

  onTournamentEnd(id) {
    const { tournament } = this.props.tournamentData;

    if (id === tournament.id) {
      this.setState({
        tournamentEnded: true
      });
    }
  }

  componentWillReceiveProps(nextProps) {
    const { tournamentData } = nextProps;
    const { tournament } = tournamentData;

    let message = '';
    if (tournament) {
      switch (tournament.status) {
        case 'Completed':
          message = 'This tournament is already complete.';
          break;

        case 'Enabled':
          message = 'This tournament has not started yet.';
          break;

        default:
          message = '';
      }
    }

    if (message) {
      this.setState({
        notificationMessage: message
      });
    }
  }

  onGameStartError(message) {
    this.setState({
      notificationMessage: message
    });
  }

  render() {
    const { user, loading: userLoading } = this.props.userData;

    const {
      tournament,
      tournamentScore,
      loading: tournamentLoading
    } = this.props.tournamentData;

    const { tournamentEnded } = this.state;

    return (
      <ErrorNotificationPage
        notificationMessage={this.state.notificationMessage}
      >
        <Helmet>
          <title>Tournament</title>
        </Helmet>
        <Grid className="main-container tournament-container">
          <div className="tournament-wrapper">
            {tournament &&
              tournament.status === 'InProgress' && (
                <div>
                  <div className="col left-panel">
                    {!userLoading && !tournamentLoading ? (
                      <Leaderboard
                        tournament={tournament}
                        userScore={tournamentScore}
                        user={user}
                        ranks={
                          (tournament.leaderBoard &&
                            tournament.leaderBoard.rankings) ||
                          []
                        }
                      />
                    ) : null}
                    <GameChat
                      isTournament
                      gameName={tournament && tournament.game.name}
                      tournamentId={tournament && tournament.id}
                      room={`trm_${this.props.match.params.tournament}`}
                    />
                  </div>
                  <div className="col right-panel">
                    <div className="community-wrapper">
                      <CommunityMessageContainer large />
                      <CloseIcon url="/" />
                    </div>
                    {tournamentLoading ? (
                      <div className="game" />
                    ) : !tournamentEnded ? (
                      <Game
                        isTournament
                        gameName={tournament && tournament.game.name}
                        tournament={tournament}
                        tournamentEnded={tournamentEnded}
                        onError={this.onGameStartError}
                      />
                    ) : (
                      <div className="tournament-completed-message">
                        <h4>
                          The tournament has completed! Join us next time and
                          find your winning moment!
                        </h4>
                      </div>
                    )}
                  </div>
                </div>
              )}
          </div>
        </Grid>
      </ErrorNotificationPage>
    );
  }
}

const QUERY_GET_TOURNAMENT = gql`
  query GetTournament($tournament: GetTournament!) {
    tournament(tournament: $tournament) {
      ...Tournament
      ...TournamentLeaderboard
    }
    tournamentScore(tournament: $tournament) {
      score
      rank
      totalRank
    }
  }
  ${fragments.tournament}
  ${fragments.tournamentLeaderboard}
`;

const SUBSCRIPTION_TOURNAMENT_UPDATES = gql`
  subscription TournamentUpdates($input: TournamentUpdatesInput!) {
    tournamentUpdates(input: $input) {
      leaderBoard {
        rankings {
          player {
            displayName
            userId
            avatarUrl
          }
          score
          rank
        }
      }
      score {
        score
        rank
        totalRank
      }
      totalPlayers
    }
  }
`;

const mapGetTournamentOptions = ({ match }) => ({
  variables: { tournament: { id: match.params.tournament } }
});

const TournamentPageWithData = compose(
  getUserData({
    name: 'userData'
  }),
  graphql(QUERY_GET_TOURNAMENT, {
    name: 'tournamentData',
    options: mapGetTournamentOptions,
    props: ({ ownProps, tournamentData }) => {
      return {
        ...ownProps,
        tournamentData,
        subscribeToTournamentUpdates: () => {
          return tournamentData.subscribeToMore({
            document: SUBSCRIPTION_TOURNAMENT_UPDATES,
            variables: {
              input: { tournamentId: ownProps.match.params.tournament }
            },
            updateQuery: (
              prev,
              {
                subscriptionData: { data: { leaderBoard, score, totalPlayers } }
              }
            ) => {
              if (!leaderBoard && !score && !totalPlayers) {
                return prev;
              }

              setTimeout(() => {
                let prevData = ownProps.client.readQuery({
                  query: QUERY_GET_TOURNAMENT,
                  variables: {
                    tournament: { id: ownProps.match.params.tournament }
                  }
                });

                const newData = cloneDeep(prevData);

                if (leaderBoard) {
                  newData.tournament.leaderBoard = leaderBoard;
                }

                if (score) {
                  newData.tournamentScore = score;
                }

                if (totalPlayers && !score) {
                  newData.tournamentScore.totalRank = `${newData.tournamentScore
                    .rank} / ${totalPlayers}`;
                }

                ownProps.client.writeQuery({
                  query: QUERY_GET_TOURNAMENT,
                  variables: {
                    tournament: { id: ownProps.match.params.tournament }
                  },
                  data: newData
                });
              }, SUBSCRIPTION_UPDATE_DELAY);

              return prev;
            }
          });
        },
        subscribeToTournamentEvents: onEndEvent => {
          return tournamentData.subscribeToMore({
            document: SUBSCRIPTION_TOURNAMENT_EVENTS,
            updateQuery: (
              prev,
              { subscriptionData: { data: { id, event } } }
            ) => {
              if (event === 'end' && onEndEvent) {
                onEndEvent(id);
              }
            }
          });
        }
      };
    }
  })
)(TournamentPage);

export default withApollo(TournamentPageWithData);



// WEBPACK FOOTER //
// ./src/containers/TournamentPage/index.js